import { useState } from "react";
import type { ParticipationPromptPublic, ParticipationStats } from "@tih/shared";
import { ApiError, postParticipationYesNo } from "../../api/client";
import { useBotProtection } from "../../hooks/useBotProtection";
import { HoneypotField } from "../forms/HoneypotField";
import { TurnstileWidget } from "../forms/TurnstileWidget";

type Props = {
  prompt: ParticipationPromptPublic;
  stats: ParticipationStats | null;
  onAnswer: () => void;
};

export function ParticipationYesNoForm({ prompt, stats, onAnswer }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [localYes, setLocalYes] = useState(stats?.yes ?? 0);
  const [localNo, setLocalNo] = useState(stats?.no ?? 0);
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  async function pick(yesNo: boolean) {
    if (!ready || busy || answered) return;
    setError(null);
    setBusy(true);
    try {
      await postParticipationYesNo({
        promptSlug: prompt.slug,
        yesNo,
        ...botFields,
      });
      if (yesNo) setLocalYes((n) => n + 1);
      else setLocalNo((n) => n + 1);
      setAnswered(true);
      onAnswer();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not save your answer");
      resetBotProtection();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="participation-yesno">
      <HoneypotField value={website} onChange={setWebsite} />
      <div className="participation-yesno-stats">
        <div>
          <b>{localYes.toLocaleString()}</b>
          <span>said yes</span>
        </div>
        <div>
          <b>{localNo.toLocaleString()}</b>
          <span>said no</span>
        </div>
      </div>
      {!answered ? (
        <div className="participation-yesno-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !ready}
            onClick={() => void pick(true)}
          >
            Yes
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={busy || !ready}
            onClick={() => void pick(false)}
          >
            Not yet
          </button>
        </div>
      ) : (
        <p className="form-success" style={{ margin: 0 }}>
          Thank you for checking in.
        </p>
      )}
      {needsTurnstile && config?.turnstileSiteKey && (
        <TurnstileWidget
          siteKey={config.turnstileSiteKey}
          onToken={onTurnstileToken}
          onExpire={onTurnstileExpire}
          onRegisterReset={registerTurnstileReset}
        />
      )}
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}
